/**
 * Entity Record Detail Component
 *
 * Read-only view of an entity record with status timeline.
 */

import EditIcon from '@mui/icons-material/Edit';
import DocumentIcon from '@mui/icons-material/Folder';
import {
  Box,
  Divider,
  EmptyState,
  GlassAlert,
  GlassButton,
  GlassCard,
  GlassChip,
  Grid,
  RecordTimeline,
  ShimmerLoader,
  type TimelineEvent,
  Typography,
  useI18n,
} from '@ops-matrix/design-system-ui';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useConsumerEntity, useConsumerRecord } from '../../hooks/useEntities';

interface EntityRecordDetailProps {
  entityId: string;
  recordId: string;
  showEditButton?: boolean;
  onEdit?: () => void;
}

function formatFieldValue(value: unknown, fieldType?: string): string | React.ReactNode {
  if (value === null || value === undefined) return '-';

  switch (fieldType) {
    case 'CURRENCY':
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
      }).format(Number(value));
    case 'DATE':
      return new Date(String(value)).toLocaleDateString();
    case 'DATETIME':
      return new Date(String(value)).toLocaleString();
    case 'BOOLEAN':
      return value ? 'Yes' : 'No';
    case 'ADDRESS':
      if (typeof value === 'object' && value !== null) {
        const addr = value as { street?: string; city?: string; state?: string; zip?: string };
        return [addr.street, addr.city, addr.state, addr.zip].filter(Boolean).join(', ');
      }
      return String(value);
    case 'PHONE': {
      const phone = String(value);
      if (phone.length === 10) {
        return `(${phone.slice(0, 3)}) ${phone.slice(3, 6)}-${phone.slice(6)}`;
      }
      return phone;
    }
    default:
      return String(value);
  }
}

function getStatusColor(status: string): 'success' | 'warning' | 'error' | 'info' | 'neutral' {
  const colorMap: Record<string, 'success' | 'warning' | 'error' | 'info' | 'neutral'> = {
    new: 'info',
    draft: 'neutral',
    submitted: 'info',
    processing: 'info',
    underwriting: 'warning',
    approved: 'success',
    denied: 'error',
    closed: 'neutral',
  };
  return colorMap[status.toLowerCase()] ?? 'neutral';
}

export default function EntityRecordDetail({
  entityId,
  recordId,
  showEditButton = true,
  onEdit,
}: EntityRecordDetailProps) {
  const navigate = useNavigate();
  const { t } = useI18n();

  const { data: entity, isLoading: entityLoading, error: entityError } = useConsumerEntity(entityId);
  const { data: record, isLoading: recordLoading, error: recordError } = useConsumerRecord(entityId, recordId);

  // Group fields by section
  const fieldGroups = useMemo(() => {
    if (!entity?.fields || !record) return [];

    const groups: { title: string; fields: { label: string; value: React.ReactNode }[] }[] = [];
    const mainFields: { label: string; value: React.ReactNode }[] = [];

    for (const field of entity.fields) {
      // Skip computed fields without values
      if (['CALCULATED', 'ROLLUP', 'FORMULA', 'AUTO_NUMBER'].includes(field.fieldType)) {
        continue;
      }

      const value = record.data[field.name];
      const formattedValue = formatFieldValue(value, field.fieldType);

      mainFields.push({
        label: field.label,
        value: formattedValue,
      });
    }

    if (mainFields.length > 0) {
      groups.push({ title: 'Details', fields: mainFields });
    }

    return groups;
  }, [entity?.fields, record]);

  // Build timeline events for RecordTimeline
  const timelineEvents = useMemo<TimelineEvent[]>(() => {
    if (!record) return [];

    const events: TimelineEvent[] = [
      {
        id: '1',
        type: 'created',
        timestamp: new Date(record.createdAt),
        description: 'Record was created',
      },
    ];

    if (record.updatedAt !== record.createdAt) {
      events.push({
        id: '2',
        type: 'updated',
        timestamp: new Date(record.updatedAt),
        description: 'Record was last updated',
      });
    }

    return events;
  }, [record]);

  const handleEdit = () => {
    if (onEdit) {
      onEdit();
    } else {
      navigate(`/records/${entityId}/${recordId}/edit`);
    }
  };

  const isLoading = entityLoading || recordLoading;
  const error = entityError || recordError;
  const status = record?.status ?? (record?.data.status as string);

  if (error) {
    return (
      <GlassAlert severity="error" visible={true} sx={{ mb: 3 }}>
        Failed to load record. Please try again later.
      </GlassAlert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'flex-start' },
          gap: 1,
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {entity?.name ?? 'Record'} Details
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
            {status &&
              (() => {
                const statusColor = getStatusColor(status);
                const chipColor = statusColor === 'neutral' ? 'default' : statusColor;
                return (
                  <GlassChip
                    label={status}
                    size="small"
                    color={chipColor as 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'}
                    sx={{ textTransform: 'capitalize', mr: 1 }}
                  />
                );
              })()}
            {record && (
              <Typography variant="body2" color="textSecondary">
                Last updated {new Date(record.updatedAt).toLocaleDateString()}
              </Typography>
            )}
          </Box>
        </Box>
        {showEditButton && entity?.allowEdit && (
          <GlassButton variant="outlined" startIcon={<EditIcon />} onClick={handleEdit}>
            {t('common.edit')}
          </GlassButton>
        )}
      </Box>

      <Grid container spacing={3}>
        {/* Main Content */}
        <Grid size={{ xs: 12, md: 8 }}>
          {isLoading ? (
            <GlassCard neonAccent="cyan" animate>
              {[1, 2, 3, 4, 5].map((i) => (
                <Box key={i} sx={{ mb: 2 }}>
                  <ShimmerLoader variant="text" width={100} height={20} />
                  <ShimmerLoader variant="text" width={200} height={24} />
                </Box>
              ))}
            </GlassCard>
          ) : (
            <>
              {fieldGroups.map((group, groupIndex) => (
                <GlassCard key={group.title} neonAccent="cyan" animate animationDelay={groupIndex * 0.1} sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    {group.title}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2}>
                    {group.fields.map((field, fieldIndex) => (
                      <Grid key={fieldIndex} size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          {field.label}
                        </Typography>
                        <Typography variant="body1">{field.value}</Typography>
                      </Grid>
                    ))}
                  </Grid>
                </GlassCard>
              ))}

              {/* Documents Section */}
              <GlassCard neonAccent="green" animate animationDelay={0.2}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    gap: 1,
                    mb: 2,
                  }}
                >
                  <Typography variant="h6">{t('common.documents')}</Typography>
                  <GlassButton size="small" onClick={() => navigate('/documents')}>
                    {t('common.viewAll')}
                  </GlassButton>
                </Box>
                <EmptyState icon={<DocumentIcon />} title={t('consumer.entity.noDocuments')} />
              </GlassCard>
            </>
          )}
        </Grid>

        {/* Sidebar - Timeline */}
        <Grid size={{ xs: 12, md: 4 }}>
          <GlassCard neonAccent="green" animate animationDelay={0.3}>
            <Typography variant="h6" gutterBottom>
              {t('consumer.entity.activityTimeline')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <RecordTimeline events={timelineEvents} loading={isLoading} neonAccent="green" variant="compact" />
          </GlassCard>
        </Grid>
      </Grid>
    </Box>
  );
}
